import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Loader2, XCircle } from 'lucide-react';

const API_URL = '';

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [orderId, setOrderId] = useState('');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    pollPaymentStatus(sessionId, 0);
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    if (attempts >= 8) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/checkout/status/${sid}`);
      if (!res.ok) throw new Error('Failed to check status');
      const data = await res.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        setOrderId(data.order_id || '');
        // Clear cart from localStorage
        localStorage.removeItem('passlux_cart');
        window.dispatchEvent(new Event('storage'));
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    }
  };

  if (status === 'loading') {
    return (
      <div className="bg-[#0a0a0a] min-h-screen pt-28 pb-20">
        <div className="max-w-lg mx-auto px-4 text-center py-20">
          <Loader2 className="w-16 h-16 text-[#2EC866] animate-spin mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Processing Payment...
          </h2>
          <p className="text-gray-400">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="bg-[#0a0a0a] min-h-screen pt-28 pb-20">
        <div className="max-w-lg mx-auto px-4 text-center py-20">
          <div className="w-20 h-20 bg-[#2EC866]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-[#2EC866]" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Order Confirmed!
          </h2>
          <p className="text-gray-400 mb-2">Thank you for your purchase.</p>
          {orderId && (
            <p className="text-gray-500 text-sm mb-8">
              Order ID: <span className="text-[#2EC866] font-mono">{orderId}</span>
            </p>
          )}
          <Link
            to="/shop"
            data-testid="continue-shopping-btn"
            className="inline-flex items-center gap-2 bg-[#2EC866] hover:bg-[#25a855] text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0a0a0a] min-h-screen pt-28 pb-20">
      <div className="max-w-lg mx-auto px-4 text-center py-20">
        <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-10 h-10 text-red-400" />
        </div>
        <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          {status === 'expired' ? 'Session Expired' : 'Payment Issue'}
        </h2>
        <p className="text-gray-400 mb-8">
          {status === 'expired'
            ? 'Your payment session has expired. Please try again.'
            : 'There was an issue verifying your payment. Please contact support if you were charged.'}
        </p>
        <Link
          to="/cart"
          className="inline-flex items-center gap-2 bg-[#2EC866] hover:bg-[#25a855] text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300"
        >
          Return to Cart
        </Link>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
